import { useEffect, useState } from "react";
import { Building2, CalendarDays, CheckCheck, Mail, MessageCircle, Phone, Trash2, Undo2, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const typeStyles = {
  Reservation: "bg-[#9A7B66]/10 text-[#9A7B66]",
  Experiences: "bg-[#5B7A66]/10 text-[#5B7A66]",
  "Events & Weddings": "bg-[#8A6AA0]/10 text-[#8A6AA0]",
  Press: "bg-[#4E6E8E]/10 text-[#4E6E8E]",
  General: "bg-[#A0988E]/10 text-[#6E675F]",
};

export default function InquiriesTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/inquiries/list").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const toggleReplied = async (q) => {
    try {
      const r = await api.put(`/admin/inquiries/${q.id}`, { replied: !q.replied });
      setItems((current) => current.map((item) => (item.id === q.id ? { ...item, replied: r.data.replied } : item)));
      toast.success(r.data.replied ? "Marked as replied" : "Marked as not replied");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeInquiry = async (q) => {
    if (!window.confirm(`Hapus inquiry dari ${q.name}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/admin/inquiries/${q.id}`);
      setItems((current) => current.filter((item) => item.id !== q.id));
      toast.success("Inquiry deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const waUrl = (q) => {
    const digits = String(q.phone || "").replace(/[^0-9]/g, "");
    if (!digits) return "";
    const text = `Hello ${q.name}, thank you for contacting Nusadira Lembongan Villa. We received your ${q.inquiry_type || "General"} inquiry and would love to assist you further.`;
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
  };

  const mailUrl = (q) => {
    if (!q.email) return "";
    const subject = `Re: Your ${q.inquiry_type || "General"} inquiry - Nusadira Lembongan Villa`;
    const body = `Dear ${q.name},\n\nThank you for contacting Nusadira Lembongan Villa. Regarding your message:\n"${q.message}"\n\n`;
    return `mailto:${q.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div data-testid="inquiries-tab">
      <h2 className="font-serif text-2xl text-[#2C2825] mb-1">Booking & Contact Inquiries</h2>
      <p className="text-sm text-[#A0988E] mb-6">Messages submitted through the Contact Us form. Reply langsung via WhatsApp atau Email.</p>
      {!items ? (
        <p className="text-sm text-[#A0988E]">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#A0988E]">No inquiries yet.</p>
      ) : (
        <>
          {(() => {
            const unreplied = items.filter((q) => !q.replied).length;
            const replied = items.length - unreplied;
            const filters = [
              ["all", `Semua (${items.length})`],
              ["unreplied", `Belum Dibalas (${unreplied})`],
              ["replied", `Sudah Dibalas (${replied})`],
            ];
            return (
              <div className="flex flex-wrap gap-2 mb-5" data-testid="inquiry-filters">
                {filters.map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`inquiry-filter-${key}`}
                    onClick={() => setFilter(key)}
                    className={`px-4 py-2 rounded-full text-[11px] font-sans font-semibold uppercase tracking-wider transition-colors ${
                      filter === key
                        ? "bg-[#9A7B66] text-white"
                        : "bg-white border border-[#E5DED4] text-[#6E675F] hover:border-[#9A7B66] hover:text-[#9A7B66]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            );
          })()}
          <div className="space-y-4">
            {items
              .filter((q) => (filter === "all" ? true : filter === "replied" ? q.replied : !q.replied))
              .map((q) => (
            <div key={q.id} data-testid={`inquiry-${q.id}`} className={`bg-white border rounded-xl overflow-hidden transition-opacity ${q.replied ? "border-[#E5DED4] opacity-75" : "border-[#E5DED4]"}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 pt-4">
                <span className="inline-flex items-center gap-2 font-sans font-semibold text-sm text-[#2C2825]">
                  <User className="w-4 h-4 text-[#9A7B66]" />
                  {q.name}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                    typeStyles[q.inquiry_type] || typeStyles.General
                  }`}
                >
                  {q.inquiry_type}
                </span>
                {q.replied && (
                  <span
                    data-testid={`inquiry-replied-badge-${q.id}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-800"
                  >
                    <CheckCheck className="w-3 h-3" /> Replied
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-[11px] text-[#A0988E] ml-auto">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {q.created_at ? new Date(q.created_at).toLocaleString() : ""}
                </span>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-1.5 px-5 mt-2.5 text-xs font-sans text-[#6E675F]">
                {q.email && (
                  <span data-testid={`inquiry-email-${q.id}`} className="inline-flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-[#9A7B66]" /> {q.email}
                  </span>
                )}
                {q.phone && (
                  <span data-testid={`inquiry-phone-${q.id}`} className="inline-flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-[#9A7B66]" /> {q.phone}
                  </span>
                )}
                {q.company && (
                  <span data-testid={`inquiry-company-${q.id}`} className="inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-[#9A7B66]" /> {q.company}
                  </span>
                )}
              </div>

              <blockquote
                data-testid={`inquiry-message-${q.id}`}
                className="mx-5 mt-3.5 border-l-2 border-[#D8CBB8] bg-[#FBF9F5] rounded-r-md px-4 py-3 text-sm text-[#6E675F] font-sans leading-relaxed"
              >
                {q.message}
              </blockquote>

              <div className="flex flex-wrap gap-2 px-5 py-4">
                {q.phone && (
                  <a
                    data-testid={`inquiry-wa-reply-${q.id}`}
                    href={waUrl(q)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 px-4 py-2 text-[11px] font-sans font-semibold uppercase tracking-wider text-[#1DA851] hover:bg-[#25D366] hover:text-white transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Reply via WhatsApp
                  </a>
                )}
                {q.email && (
                  <a
                    data-testid={`inquiry-email-reply-${q.id}`}
                    href={mailUrl(q)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#9A7B66]/10 border border-[#9A7B66]/30 px-4 py-2 text-[11px] font-sans font-semibold uppercase tracking-wider text-[#9A7B66] hover:bg-[#9A7B66] hover:text-white transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" /> Reply via Email
                  </a>
                )}
                <span className="flex-1" />
                <button
                  data-testid={`inquiry-replied-toggle-${q.id}`}
                  onClick={() => toggleReplied(q)}
                  title={q.replied ? "Tandai belum dibalas" : "Tandai sudah dibalas"}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[11px] font-sans font-semibold uppercase tracking-wider transition-colors ${
                    q.replied
                      ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                      : "bg-white border-[#E5DED4] text-[#A0988E] hover:border-green-300 hover:text-green-700"
                  }`}
                >
                  {q.replied ? <Undo2 className="w-3.5 h-3.5" /> : <CheckCheck className="w-3.5 h-3.5" />}
                  {q.replied ? "Replied" : "Mark Replied"}
                </button>
                <button
                  data-testid={`inquiry-delete-${q.id}`}
                  onClick={() => removeInquiry(q)}
                  title="Hapus inquiry"
                  className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-4 py-2 text-[11px] font-sans font-semibold uppercase tracking-wider text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
            {items.filter((q) => (filter === "all" ? true : filter === "replied" ? q.replied : !q.replied)).length === 0 && (
              <p className="text-sm text-[#A0988E]" data-testid="inquiry-filter-empty">
                {filter === "unreplied" ? "Semua inquiry sudah dibalas." : filter === "replied" ? "Belum ada inquiry yang dibalas." : "No inquiries yet."}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
